import React from "react";

// =============================================================================
// PortalLayout — inline SVG icon set
// -----------------------------------------------------------------------------
// Self-contained 16px stroke icons (Streamline-ish, matching the Figma nav) so
// the layout template carries no icon-library dependency and renders the same
// in isolation. `currentColor` is used throughout so colour is driven by CSS.
// =============================================================================

export type IconName =
  | "search"
  | "dashboard"
  | "requests"
  | "offers"
  | "invoices"
  | "aircraft"
  | "support"
  | "messages"
  | "documentation"
  | "settings"
  | "plus"
  | "logout"
  | "bell"
  | "reload"
  | "clock"
  | "chevron-down"
  | "menu"
  | "panel"
  | "expand"
  | "eye"
  | "check"
  | "edit"
  | "trash"
  | "close"
  | "car"
  | "yacht"
  | "calendar"
  | "tag"
  | "route"
  | "layers"
  | "dashboard-filled";

interface IconProps {
  size?: number;
  className?: string;
}

const svg = (size: number, className: string | undefined, children: React.ReactNode) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.4}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

const PATHS: Record<IconName, React.ReactNode> = {
  search: (
    <>
      <circle cx="7" cy="7" r="4.25" />
      <path d="M10.2 10.2 13.5 13.5" />
    </>
  ),
  dashboard: (
    <>
      <rect x="2" y="2" width="5" height="5" rx="1" />
      <rect x="9" y="2" width="5" height="5" rx="1" />
      <rect x="2" y="9" width="5" height="5" rx="1" />
      <rect x="9" y="9" width="5" height="5" rx="1" />
    </>
  ),
  requests: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M2 8h12M8 2c1.7 1.7 2.6 3.8 2.6 6S9.7 12.3 8 14C6.3 12.3 5.4 10.2 5.4 8S6.3 3.7 8 2Z" />
    </>
  ),
  offers: (
    <>
      <path d="M8 1.8l1.6 1.2 2-.2.6 1.9 1.7 1-.8 1.8.8 1.8-1.7 1-.6 1.9-2-.2L8 14.2 6.4 13l-2 .2-.6-1.9-1.7-1 .8-1.8-.8-1.8 1.7-1 .6-1.9 2 .2Z" />
      <path d="M6 8l1.4 1.4L10.2 6.6" />
    </>
  ),
  invoices: (
    <>
      <path d="M4 1.8h5.2L13 5.4V14a.6.6 0 0 1-.6.6H4a.6.6 0 0 1-.6-.6V2.4A.6.6 0 0 1 4 1.8Z" />
      <path d="M9 1.8V5.4h3.6M5.6 8.5h4.8M5.6 11h4.8" />
    </>
  ),
  aircraft: (
    <>
      <path d="M8 1.6c.7 0 1.1.7 1.1 1.8v2.3l4.4 2.6v1.4L9.1 8.6v2.6l1.4 1v1.2L8 12.7l-2.5.7v-1.2l1.4-1V8.6L2.5 9.7V8.3L6.9 5.7V3.4c0-1.1.4-1.8 1.1-1.8Z" />
    </>
  ),
  support: (
    <>
      <circle cx="8" cy="8" r="6" />
      <circle cx="8" cy="8" r="2.4" />
      <path d="M3.8 3.8 6.3 6.3M9.7 9.7l2.5 2.5M12.2 3.8 9.7 6.3M6.3 9.7 3.8 12.2" />
    </>
  ),
  messages: (
    <>
      <path d="M2.2 4.2a1 1 0 0 1 1-1h7.2a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H6l-2.4 2V9.2H3.2a1 1 0 0 1-1-1Z" />
      <path d="M11.4 6.2h1.4a1 1 0 0 1 1 1v3.4a1 1 0 0 1-1 1h-.4v1.8l-2.2-1.8" />
    </>
  ),
  documentation: (
    <>
      <path d="M3 2.4h6.4L13 6v7.6a.6.6 0 0 1-.6.6H3a.6.6 0 0 1-.6-.6V3a.6.6 0 0 1 .6-.6Z" />
      <path d="M9 2.4V6h3.6M6 8.5l1.3 1.3L9.6 7" />
    </>
  ),
  settings: (
    <>
      <path d="M2.3 5h7M2.3 11h4.4" />
      <circle cx="11" cy="5" r="1.8" />
      <circle cx="9" cy="11" r="1.8" />
    </>
  ),
  plus: <path d="M8 3v10M3 8h10" />,
  logout: (
    <>
      <path d="M6.2 2.4H3.4a1 1 0 0 0-1 1v9.2a1 1 0 0 0 1 1h2.8" />
      <path d="M9.2 11l3-3-3-3M12.2 8H6" />
    </>
  ),
  bell: (
    <>
      <path d="M4 6.8a4 4 0 0 1 8 0c0 3.2 1 4 1 4H3s1-.8 1-4Z" />
      <path d="M6.6 13a1.6 1.6 0 0 0 2.8 0" />
    </>
  ),
  reload: (
    <>
      <path d="M13 8a5 5 0 1 1-1.5-3.6" />
      <path d="M13 2.2v3h-3" />
    </>
  ),
  clock: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.6V8l2.4 1.4" />
    </>
  ),
  "chevron-down": <path d="M4 6l4 4 4-4" />,
  menu: <path d="M2.5 4h11M2.5 8h11M2.5 12h11" />,
  panel: (
    <>
      <rect x="2.2" y="3" width="11.6" height="10" rx="1.4" />
      <path d="M6 3v10" />
    </>
  ),
  expand: <path d="M5.5 6.5 8 4l2.5 2.5M10.5 9.5 8 12 5.5 9.5" />,
  eye: (
    <>
      <path d="M1.6 8C2.9 5.5 5.2 4 8 4s5.1 1.5 6.4 4c-1.3 2.5-3.6 4-6.4 4S2.9 10.5 1.6 8Z" />
      <circle cx="8" cy="8" r="2.1" />
    </>
  ),
  check: <path d="M3 8.5 6.4 12 13 4.5" />,
  edit: (
    <>
      <path d="M9.5 3.6 12.4 6.5" />
      <path d="M3 13l.7-3 7.2-7.2a1.2 1.2 0 0 1 1.7 0l1.6 1.6a1.2 1.2 0 0 1 0 1.7L7 13.3 3 13Z" />
    </>
  ),
  trash: (
    <>
      <path d="M2.6 4.5h10.8" />
      <path d="M5.6 4.5V3a.8.8 0 0 1 .8-.8h3.2a.8.8 0 0 1 .8.8v1.5" />
      <path d="M4.1 4.5l.7 8.7a.8.8 0 0 0 .8.7h4.8a.8.8 0 0 0 .8-.7l.7-8.7" />
      <path d="M6.6 7v4M9.4 7v4" />
    </>
  ),
  close: <path d="M4 4l8 8M12 4l-8 8" />,
  car: (
    <>
      <path d="M3 9.3 4.2 6a1 1 0 0 1 .94-.66h5.72A1 1 0 0 1 11.8 6L13 9.3" />
      <path d="M2.4 9.3h11.2v2a.5.5 0 0 1-.5.5h-.8M4.1 11.8h-1.2a.5.5 0 0 1-.5-.5v-2" />
      <circle cx="4.9" cy="11.8" r="1.05" />
      <circle cx="11.1" cy="11.8" r="1.05" />
    </>
  ),
  yacht: (
    <>
      <path d="M8 2.3 12 9.4H8z" />
      <path d="M8 4.4 4.6 9.4H8" />
      <path d="M2.6 10.4h10.8l-1.15 2.2a.6.6 0 0 1-.53.33H4.28a.6.6 0 0 1-.53-.33L2.6 10.4Z" />
    </>
  ),
  calendar: (
    <>
      <rect x="2.4" y="3.2" width="11.2" height="10.4" rx="1" />
      <path d="M2.4 6h11.2" />
      <path d="M5.2 2v2.4M10.8 2v2.4" />
    </>
  ),
  tag: (
    <>
      <path d="M7.8 2.4H3.4a1 1 0 0 0-1 1v4.4a1 1 0 0 0 .3.7l5.4 5.4a1 1 0 0 0 1.4 0l4.1-4.1a1 1 0 0 0 0-1.4L8.5 2.7a1 1 0 0 0-.7-.3Z" />
      <circle cx="5.5" cy="5.5" r="0.9" />
    </>
  ),
  route: (
    <>
      <circle cx="4" cy="11.5" r="1.4" />
      <circle cx="12" cy="4.5" r="1.4" />
      <path d="M5.3 10.6 10.7 5.4" />
    </>
  ),
  layers: (
    <>
      <path d="M8 2.2 14 5.4 8 8.6 2 5.4 8 2.2Z" />
      <path d="M2 8.2l6 3.2 6-3.2" />
      <path d="M2 11l6 3.2 6-3.2" />
    </>
  ),
  // Solid/filled twin of `dashboard` — used as the breadcrumb "home" marker.
  "dashboard-filled": (
    <>
      <rect x="2" y="2" width="5" height="5" rx="1" fill="currentColor" stroke="none" />
      <rect x="9" y="2" width="5" height="5" rx="1" fill="currentColor" stroke="none" />
      <rect x="2" y="9" width="5" height="5" rx="1" fill="currentColor" stroke="none" />
      <rect x="9" y="9" width="5" height="5" rx="1" fill="currentColor" stroke="none" />
    </>
  ),
};

export const Icon = ({ name, size = 16, className }: IconProps & { name: IconName }) =>
  svg(size, className, PATHS[name]);

export default Icon;
